/**
 * nav-config.js — Enterprise Workspace navigation blueprint.
 *
 * Definieert:
 *  - NAV_GROUPS: hoofdgroepen + submodules (inklapbaar per groep)
 *  - NAV_ITEMS_FLAT: platte map voor breadcrumb + zoekresultaat resolutie
 *  - QUICK_ACTIONS: acties in het rechterbovenhoek Quick-Actions dropdown
 *  - CATEGORY_ICONS: mapping van search-category → lucide-icon
 *
 * BELANGRIJK — Backward compatibiliteit:
 *  - De `key` van iedere nav-item is IDENTIEK aan de key gebruikt in
 *    AdminPage's ADMIN_MENU. Zo hoeft AdminPage.jsx niet te wijzigen.
 *  - Groepering is puur VISUEEL; de section-router blijft ongewijzigd.
 *
 * Toekomstbestendig — nieuwe modules kunnen eenvoudig worden toegevoegd:
 *  - Voeg een nieuwe item toe aan de juiste groep, of maak nieuwe groep aan.
 *  - Voeg een quick-action toe met `to` (nav-key) en optioneel `openNew: true`.
 */
import {
  LayoutDashboard, FileText, Users, Truck, Bell, Globe, ShoppingBag,
  UserCog, Settings, ScrollText, PhoneIncoming, ClipboardList,
  ClipboardCheck, Receipt, Tag, Package, Layers, Warehouse, MessageSquare,
  AlertTriangle,
  Plus, FilePlus, UserPlus, PackagePlus, PlaneTakeoff, Container,
  Megaphone, FileStack, BarChart3, ShieldCheck, Building2,
} from "lucide-react";

/** Groepen in de zijbalk — order = weergaveorder.
 * Sprint A Finalisatie (22 Feb 2026): nieuwe vaste 8-groep volgorde.
 * Verkoop groep is verwijderd; Offertes + Bestellingen zijn ondergebracht
 * in Financieel. Deze frontend-config blijft synchroon met de backend
 * `admin_navigation_registry.bootstrap_default_navigation()`.
 */
export const NAV_GROUPS = [
  {
    key: "overzicht",
    label: "Overzicht",
    items: [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard:view" },
    ],
  },
  {
    key: "crm",
    label: "CRM",
    items: [
      { key: "klanten",        label: "Klanten",               icon: Users,          perm: "customers:view" },
      { key: "agents",         label: "Agents",                icon: Users,          perm: "access:owner" },
      { key: "leads",          label: "Leads & Aanmeldingen",  icon: UserPlus,       perm: "customers:view" },
      { key: "call_log",       label: "Telefoonregistratie",   icon: PhoneIncoming,  perm: "pickup:view" },
    ],
  },
  {
    key: "warehouse",
    label: "Warehouse",
    items: [
      { key: "warehouse",      label: "Magazijn",              icon: Warehouse,   perm: "quotes:view" },
      { key: "prealerts", label: "Prealerts", icon: Package, perm: "shipments:lookup" },
      { key: "tracking_labels",label: "Tracking & Labels",     icon: Tag,         perm: "quotes:view" },
      { key: "agent_operations", label: "Agent-operaties", icon: ShieldCheck, perm: "tracking:manage" },
      { key: "agent_reconciliation", label: "Agent-reconciliatie", icon: ClipboardCheck, perm: "tracking:manage" },
    ],
  },
  {
    key: "documenten",
    label: "Documenten",
    items: [
      { key: "documenten",     label: "Documenten & Formulieren", icon: FileStack,  perm: "documents:view" },
    ],
  },
  {
    key: "logistiek",
    label: "Logistiek",
    items: [
      { key: "pickup",         label: "Pickup & Delivery",     icon: Truck,       perm: "pickup:view" },
      { key: "consolidations", label: "Consolidaties & Vluchten", icon: Layers,   perm: "consolidations:view" },
      { key: "consolidation_automation", label: "Consolidation Automation", icon: ClipboardCheck, perm: "tracking:view" },
      { key: "status_generator", label: "Status Generator",    icon: ClipboardList, perm: "status:view" },
    ],
  },
  {
    key: "financieel",
    label: "Financieel",
    items: [
      // Sprint A Finalisatie: Offertes + Bestellingen ondergebracht in Financieel
      { key: "offertes",       label: "Offertes",              icon: FileText,       perm: "quotes:view" },
      { key: "facturen",       label: "Facturen",              icon: Receipt,        perm: "quotes:view" },
      { key: "bestellingen",   label: "Bestellingen",          icon: Package,        perm: "webshop:orders_view" },
      { key: "debiteuren",     label: "Debiteurenbeheer",      icon: Users,          perm: "quotes:view" },
      // Feb 2026 IA-refactor — 'Betalingsregelingen' is nu een tab BINNEN
      // Debiteurenbeheer. Deze regel is bewust uitgecommentarieerd (niet
      // verwijderd) zodat deep-links en backend-routes onaangetast blijven.
      // { key: "regelingen", label: "Betalingsregelingen", icon: ClipboardCheck, perm: "quotes:view" },
    ],
  },
  {
    key: "communicatie",
    label: "Communicatie",
    items: [
      { key: "communicatie",   label: "Communicatiecentrum",   icon: MessageSquare, perm: "quotes:view" },
      { key: "website_messages", label: "Websiteberichten", icon: MessageSquare, perm: "website_messages:view" },
      { key: "reminders",      label: "Reminders",             icon: Bell,          perm: "reminders:view" },
      { key: "bounce_center",  label: "Bounce Management",     icon: AlertTriangle, perm: "quotes:view" },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    items: [
      { key: "websitebeheer",  label: "Websitebeheer",         icon: Globe,        perm: "website:view" },
      { key: "webshopbeheer",  label: "Webshopbeheer",         icon: ShoppingBag,  perm: "webshop:view" },
    ],
  },
  // Placeholder — activeer als de rapportages-module live gaat.
  // {
  //   key: "rapportages",
  //   label: "Rapportages",
  //   items: [ { key: "reports", label: "Rapportages", icon: BarChart3, perm: "reports:view" } ],
  // },
  {
    key: "beheer",
    label: "Beheer",
    items: [
      { key: "medewerkers",    label: "Medewerkers & Rollen",  icon: UserCog,      perm: "users:view" },
      { key: "instellingen",   label: "Instellingen",          icon: Settings,     perm: "settings:view" },
      { key: "audit",          label: "Activiteitenlogboek",   icon: ScrollText,   perm: "audit:view" },
    ],
  },
];

/** Platte lookup: nav-key → { group, item } — voor breadcrumbs + zoek. */
export const NAV_ITEMS_FLAT = (() => {
  const map = {};
  NAV_GROUPS.forEach((g) => {
    (g.items || []).forEach((it) => {
      map[it.key] = { group: g, item: it };
    });
  });
  return map;
})();

/** Quick-Actions dropdown items (rechtsboven). */
export const QUICK_ACTIONS = [
  { key: "new_invoice",   label: "Nieuwe factuur",     icon: Receipt,    to: "facturen",        hash: "nieuw",  perm: "quotes:view" },
  { key: "new_quote",     label: "Nieuwe offerte",     icon: FilePlus,   to: "offertes",        hash: "nieuw",  perm: "quotes:view" },
  { key: "new_customer",  label: "Nieuwe klant",       icon: UserPlus,   to: "klanten",         hash: "nieuw",  perm: "customers:view" },
  { key: "new_pickup",    label: "Nieuwe pickup",      icon: Truck,      to: "pickup",          hash: "nieuw",  perm: "pickup:view" },
  { key: "new_booking",   label: "Nieuwe boeking",     icon: Package,    to: "documenten",      hash: "nieuw-boeking", perm: "customers:view" },
  { key: "new_warehouse", label: "Nieuw warehouse-item", icon: PackagePlus, to: "warehouse",    hash: "nieuw",  perm: "quotes:view" },
  { key: "new_container", label: "Nieuwe container",   icon: Container,  to: "consolidations",  hash: "nieuw",  perm: "consolidations:view" },
  { key: "new_flight",    label: "Nieuwe vlucht",      icon: PlaneTakeoff, to: "consolidations", hash: "vlucht-nieuw", perm: "consolidations:view" },
];

/** Search category icons — mapped names uit backend admin_global_search.py */
export const CATEGORY_ICONS = {
  customers: Users,
  invoices: Receipt,
  quotes: FileText,
  bookings: Truck,
  orders: ShoppingBag,
  tracking: Tag,
  containers: Container,
  flights: PlaneTakeoff,
  reminders: Bell,
  emails: MessageSquare,
  stripe_sessions: ShieldCheck,
  users: UserCog,
};

/**
 * Resolve breadcrumbs voor een gegeven active-nav-key.
 * Retourneert een lijst [{ label, onClick? }] — de laatste is de active page.
 */
export function resolveBreadcrumbs(activeKey, entity) {
  const entry = NAV_ITEMS_FLAT[activeKey];
  const crumbs = [{ label: "Backoffice", key: null }];
  if (!entry) {
    crumbs.push({ label: activeKey || "Onbekend", key: activeKey, active: true });
    return crumbs;
  }
  if (entry.group && entry.group.key !== "overzicht") {
    crumbs.push({ label: entry.group.label, key: null });
  }
  crumbs.push({
    label: entry.item.label,
    key: entry.item.key,
    active: !entity,
  });
  if (entity && entity.label) {
    crumbs.push({ label: entity.label, key: null, active: true });
  }
  return crumbs;
}
